import { db } from "../../db/localDB";
import { crearDisentimientoBac, getDisentimientosBac } from "../api/disentimientoAPI";

// ════════════════════════════════════════════════════════════════
// DISENTIMIENTO — cola offline y caché local (dentro de BAC)
// ════════════════════════════════════════════════════════════════
// Mismos estados de "sincronizado" que el resto de BAC (ver useSync.js):
//   0 = pendiente por enviar · 3 = error de datos (motivo en ultimoError)
// A diferencia de las visitas, lo ya sincronizado se BORRA del
// dispositivo: la firma en base64 es lo que más pesa y ya está segura
// en el servidor.

const CLAVE_CACHE = "lista";

function motivoLegible(error) {
  return error?.response?.data?.message || error?.message || "Error desconocido";
}

export async function guardarDisentimientoOfflineBac(payload) {
  // Guardia contra doble toque: el mismo "codigo" nunca entra dos veces
  const existente = await db.bac_disentimiento_pendientes.where("codigo").equals(payload.codigo).first();
  if (existente) return existente.id;
  return db.bac_disentimiento_pendientes.add({
    ...payload,
    sincronizado: 0,
    createdAt: new Date().toISOString(),
  });
}

export async function leerDisentimientosPendientesBac() {
  return db.bac_disentimiento_pendientes.toArray();
}

export async function contarDisentimientosPendientesBac() {
  return db.bac_disentimiento_pendientes.where("sincronizado").equals(0).count();
}

export async function cachearDisentimientosBac(registros) {
  try {
    await db.bac_disentimiento_cache.put({
      clave: CLAVE_CACHE,
      registros: JSON.stringify(registros || []),
      cachedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.warn("⚠️ No se pudo guardar la lista de disentimientos en el dispositivo:", e.message);
  }
}

export async function leerDisentimientosCacheBac() {
  try {
    const rec = await db.bac_disentimiento_cache.get(CLAVE_CACHE);
    return rec ? { registros: JSON.parse(rec.registros), cachedAt: rec.cachedAt } : { registros: [], cachedAt: null };
  } catch {
    return { registros: [], cachedAt: null };
  }
}

// Sube la cola. La llama sincronizarPendientesBac() (useSync.js), así
// que corre sola al volver la señal, al pulsar "Sincronizar" en el panel
// de pendientes, y cada vez que BAC sincroniza.
let sincronizando = false;
export async function sincronizarDisentimientosBac() {
  if (sincronizando) return 0;
  sincronizando = true;
  let sincronizados = 0;
  try {
    const pendientes = await db.bac_disentimiento_pendientes
      .filter(r => r.sincronizado === 0)
      .toArray();

    for (const r of pendientes) {
      try {
        const { id, sincronizado, createdAt, ultimoError, ...payload } = r;
        await crearDisentimientoBac(payload); // el backend no duplica si el "codigo" ya existe
        await db.bac_disentimiento_pendientes.delete(r.id);
        sincronizados++;
      } catch (e) {
        const status = e?.response?.status;
        if ([400, 404, 409, 413, 422].includes(status)) {
          const motivo = status === 413
            ? "La firma es demasiado pesada para el servidor — hay que volver a registrar la constancia."
            : motivoLegible(e);
          await db.bac_disentimiento_pendientes.update(r.id, { sincronizado: 3, ultimoError: motivo });
          window.dispatchEvent(new CustomEvent("bac-error-sincronizacion", {
            detail: { tabla: "bac_disentimiento_pendientes", id: r.id, motivo, codigo: r.codigo },
          }));
        } else {
          // Sin señal o servidor caído — se queda en 0 para el próximo intento
          console.error("❌ Error sync disentimiento:", e.message);
        }
      }
    }

    if (sincronizados > 0) {
      console.log(`✅ Sync disentimientos: ${sincronizados}`);
      // Refresca la lista guardada para que la pestaña muestre ya lo subido
      try {
        const res = await getDisentimientosBac();
        await cachearDisentimientosBac(res.data || []);
      } catch { /* no crítico: se refresca al abrir la pestaña */ }
      window.dispatchEvent(new CustomEvent("disentimiento-sync-completado"));
    }
    return sincronizados;
  } finally {
    sincronizando = false;
  }
}
